import {
  ROLE_ADMIN,
  ROLE_STUDENT,
} from '../global';

import { environment } from '../../../environments/environment';

/**
 * Represents a user for the application.
 */
export class User {
  /**
   * The user's unique identifier. This is their document ID in Firestore, as well
   * as the ID of their authentication account.
   */
  id: string;

  /**
   * The user's email.
   */
  email: string;

  /**
   * The user's first name.
   */
  firstName: string;

  /**
   * The user's last name.
   */
  lastName: string;

  /**
   * The user's role. Role determines the user's permissions level, enabling or disabling
   * them from seeing specific pages and components.
   */
  role: string;

  /**
   * A list of IDs representing the user's assigned patients. The IDs are used to get the
   * full patient document from Firestore.
   */
  patientIds: string[];

  /**
   * A list of IDs representing the user's students. The IDs are used to get the full student
   * documents from Firestore.
   */
  studentIds?: string[];

  emailDomains = environment.customer.emailDomains;

  constructor(email?: string, firstName?: string, lastName?: string, role?: string) {
    this.email = email ? email : '';
    this.firstName = firstName ? firstName : '';
    this.lastName = lastName ? lastName : '';
    this.role = role ? role : ROLE_STUDENT; // user objects default to "student" role

    this.patientIds = [];
    this.id = 'n/a';      // will be replaced for real users with the id from the Auth system.
  }

  /**
   * Sets the user's ID to the ID passed to the function.
   *
   * @param id the user's new unique identifier.
   */
  setId(id: string) {
    this.id = id;
  }

  /**
   * @returns the user's ID.
   */
  getId() {
    return this.id;
  }

  /**
   * Cleans the User object by ensuring no whitespaces are on either end
   * of the properties, and that there are no spaces in the ID.
   */
  sanitize() {
    this.id = this.id.replace(/\s/g, '');
    this.firstName = this.firstName.trim();
    this.lastName = this.lastName.trim();
  }

  /**
   * @returns The user's first name followed by their last name.
   */
  get firstNameLastName() {
    return this.firstName + ' ' + this.lastName;
  }

  /**
   * @returns The user's last name followed by a comma, followed by their first name.
   */
  get lastNameFirstName() {
    return this.lastName + ', ' + this.firstName;
  }

  /**
   * Used when creating new users through uploading a CSV. See the student-list component
   * for more information in how this accessor is used.
   *
   * @returns Whether the user's email is a Calvin University email or not.
   */
  get hasValidEmail() {
    return this.emailDomains.some((domain) => this.email.toLocaleLowerCase().endsWith(domain));
  }

  /**
   * @returns Whether user is an admin or not.
   */
  get isAdmin() {
    return this.role.toLocaleLowerCase() == ROLE_ADMIN;
  }
}
